import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Button } from '@packages/utils';
import { EIconNames } from 'components/Base/Icon';
import {
  EEventFilterParams,
  EEventSortParams,
  EVENT_NO_ELC_FILTER_VALUE,
  TEventDataUI,
} from 'data/types/events.types';
import { Table } from 'components/Base/Table';
import {
  useGetEventCitiesQuery,
  useGetEventHostsQuery,
  useGetEventLocationQuery,
  useGetEventsQuery,
} from 'redux/apiSlice/events.slice';
import { useGetElcIdentitiesQuery } from 'redux/apiSlice/elcs.slice';
import useTable from 'components/Base/Table/hooks/useTable';
import CardContent from 'components/Base/Layout/Content/CardContent';
import PageWrapper from 'components/Base/PageWrapper';
import { ETableStoragePaths } from 'components/Base/Table/Table.types';
import { TBackButtonState } from 'components/Base/PageWrapper/PageWrapper.types';
import { TRtkErrorType, TTableFilterDataUI } from 'data/types/generic.types';
import { ETagType } from 'data/types/tags.types';
import { useGetTagsListQuery } from 'redux/apiSlice/tags.slice';
import { useCheckPermission } from 'hooks/useCheckPermission';
import ActiveFilterToggle from 'components/Shared/ActiveFilterToggle';
import useDidUpdate from 'hooks/useDidUpdate';
import useSessionDateSort from 'components/Shared/SessionDateSort/useSessionDateSort';
import { DEFAULT_DATE_SORT_COLUMN } from 'components/Shared/SessionDateSort/sessionDateSort.helpers';
import CreateEventModal from '../CreateEvent';
import useGetColumnsData from './EventList.data';
import SessionDates from './components/SessionDates';
import EventExport from './components/EventExport';

const EventList = () => {
  const { defaultColumns } = useGetColumnsData();
  const {
    page,
    pageSize,
    sortColumn,
    setPage,
    setPageSize,
    setSortColumn,
    handleTableChange,
    selectedFilters,
    hasSelectedFilter,
    hasFilters,
    columns,
    setColumns,
    onFiltersReset,
    selectedRows,
    setSelectedRows,
  } = useTable<TEventDataUI>({
    storagePath: ETableStoragePaths.EVENTS,
    defaultColumns,
    // Next session date ascending on load and after Reset Filters (GROW-4270)
    defaultSortColumn: DEFAULT_DATE_SORT_COLUMN,
  });

  // The Event Date column is a regular server-side sorter on the backend's
  // next session date (PART_DATE), sent as sortField + sortDirection.
  const { isDateSorted, resetSort, onTableChange } =
    useSessionDateSort<TEventDataUI>({
      sortColumn,
      setSortColumn,
      setPage,
      handleTableChange,
    });

  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.events.list',
  });

  const navigate = useNavigate();
  const [openCreateEventModal, setOpenCreateEventModal] =
    useState<boolean>(false);

  const [showMyEventsOnly, setShowMyEventsOnly] = useState(() => {
    const storedValue = localStorage.getItem('events_show_my_events_only');
    if (storedValue === null) {
      localStorage.setItem('events_show_my_events_only', JSON.stringify(false));
      return false;
    }
    return JSON.parse(storedValue);
  });

  const { data, isFetching, isLoading, error } = useGetEventsQuery({
    page,
    pageSize,
    sortColumn,
    filters: selectedFilters,
    myEvents: showMyEventsOnly,
  });

  const { data: citiesFilters } = useGetEventCitiesQuery();
  const { data: locationFilters } = useGetEventLocationQuery();

  const { data: hostsFilters } = useGetEventHostsQuery();

  const { data: tagsFilters } = useGetTagsListQuery(ETagType.EVENT);

  const { data: elcIdentities } = useGetElcIdentitiesQuery();

  // Prepend a "Not in an ELC" sentinel to the ELC filter options; the
  // eventFilterConverter splits it back into `includeNoElc: true`.
  const elcFilters = useMemo<TTableFilterDataUI[] | undefined>(() => {
    if (!elcIdentities) return undefined;
    return [
      {
        text: t('table.NOT_IN_ELC'),
        value: EVENT_NO_ELC_FILTER_VALUE,
      },
      ...elcIdentities.map(elc => ({ text: elc.label, value: elc.value })),
    ];
  }, [elcIdentities, t]);

  useEffect(() => {
    setColumns(
      columns.map(item => {
        if (item.key === EEventFilterParams.HOST) {
          item.filters = hostsFilters;
        }
        if (item.key === EEventFilterParams.CITY) {
          item.filters = citiesFilters;
        }
        if (item.key === EEventFilterParams.LOCATION) {
          item.filters = locationFilters;
        }
        if (item.key === EEventFilterParams.TAGS) {
          item.filters = tagsFilters;
        }
        if (item.key === EEventFilterParams.ELC) {
          item.filters = elcFilters;
        }
        return item;
      }),
    );
  }, [citiesFilters, hostsFilters, tagsFilters, locationFilters, elcFilters]);

  useEffect(() => {
    setColumns(
      columns.map(item => {
        if (item.key === EEventSortParams.NEXT_SESSION_DATE) {
          // While sorted by date, mark each row's next session using the
          // backend's nextSessionPart flag (GROW-4274).
          item.render = (
            sessionDates: TEventDataUI['sessionDates'],
            record: TEventDataUI,
          ) => (
            <SessionDates
              sessionDates={sessionDates}
              nextSessionPart={record.nextSessionPart}
              highlightNextSession={isDateSorted}
            />
          );
        }
        return item;
      }),
    );
  }, [isDateSorted]);

  const navigateToEventDetails = (record: TEventDataUI) => {
    return {
      onClick: () => {
        navigate(record.eventId, {
          state: {
            backUrl: window.location.pathname,
            backButtonSuffix: t('TITLE'),
          } as TBackButtonState,
        });
      },
    };
  };

  const handleMyEventsToggle = (checked: boolean) => {
    setShowMyEventsOnly(checked);
    localStorage.setItem('events_show_my_events_only', JSON.stringify(checked));
    // A different set of events is listed: back to the default sort and the
    // first page (GROW-4271).
    resetSort();
  };

  const isEmpty =
    !data?.content.length &&
    !hasSelectedFilter &&
    !showMyEventsOnly &&
    !isFetching;

  const canAddEvent = useCheckPermission('EVENT_ADD');

  // A new filter or the "Show only my Events" toggle can hide selected rows;
  // start the selection over so Export Selected never sends rows that are no
  // longer in the list. Keyed on the applied filter values because every
  // table change (paging, sorting) hands over a new filters object, with a
  // null for each unfiltered column.
  const listedEventsKey = JSON.stringify([
    showMyEventsOnly,
    Object.entries(selectedFilters)
      .filter(([, value]) => value?.length)
      .sort(([a], [b]) => a.localeCompare(b)),
  ]);
  useDidUpdate(() => {
    setSelectedRows({ selectedRowsKeys: [], selectedRowsRecords: [] });
  }, [listedEventsKey]);

  const tableHeaderActions = [
    <EventExport
      key="export-actions"
      selectedRowKeys={selectedRows.selectedRowsKeys}
      selectedFilters={selectedFilters}
      sortColumn={sortColumn}
      myEvents={showMyEventsOnly}
      columnKeys={columns.map(column => String(column.key))}
      disabled={!data?.content?.length}
    />,
    <ActiveFilterToggle
      key="my-events-toggle"
      checked={showMyEventsOnly}
      onChange={handleMyEventsToggle}
      label="Show only my Events:"
      tooltipText="Shows events assigned to you or created by you"
      storageKey="events_show_my_events_only"
      defaultValue={false}
      data-cy="events-show-my-events-toggle"
    />,
  ];

  return (
    <>
      <PageWrapper
        title={t('TITLE')}
        {...(canAddEvent && {
          actions: (
            <Button
              onClick={() => setOpenCreateEventModal(true)}
              color="primary"
              type="primary"
            >
              {t('actions.create-event.CREATE_EVENT_BTN')}
            </Button>
          ),
        })}
        isLoading={isLoading}
        error={error as TRtkErrorType}
        emptyWrapper={{
          isEmpty,
          alt: 'Handbag',
          icon: EIconNames.HANDBAG,
          ...(canAddEvent && { btnText: t('empty-view.BTN_TEXT') }),
          emptyText: t('empty-view.EMPTY_TEXT'),
          onClick: () => setOpenCreateEventModal(true),
        }}
      >
        <CardContent>
          <Table<TEventDataUI>
            headerActions={tableHeaderActions}
            onFiltersReset={onFiltersReset}
            hasFilters={hasFilters}
            rowKey={(record: TEventDataUI) => record.eventId}
            currentPage={page}
            columns={columns}
            dataSource={data?.content}
            rowClassName="pointer"
            onRow={navigateToEventDetails}
            loading={isFetching}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalDataCount={data?.totalElements}
            onChange={onTableChange}
            storagePath={ETableStoragePaths.EVENTS}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
          />
        </CardContent>
      </PageWrapper>
      {openCreateEventModal && (
        <CreateEventModal
          open={openCreateEventModal}
          setOpen={setOpenCreateEventModal}
        />
      )}
    </>
  );
};

export default EventList;
